import { useState, useEffect } from "react";
import SiderBar from "../../components/siderbarcomponents";
import {
  subscribeToCasasPersonal, addCasaPersonal, deleteCasaPersonal, updateCasaPersonal, Casa,
} from "../../firebase/firebaseService";
import {
  FaHome, FaPlus, FaTrash, FaSearch, FaTimes, FaEdit,
  FaBuilding, FaCalendarAlt, FaEnvelope, FaMapMarkerAlt, FaPhone, FaUser,
} from "react-icons/fa";

const fmt = (d: Date) =>
  d.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" });

const toInput = (d: Date) => {
  try { return d.toISOString().split("T")[0]; } catch { return ""; }
};

const emptyForm = {
  nombreDueno: "", email: "", telefono: "", direccion: "",
  fechaRegistro: new Date().toISOString().split("T")[0],
  precioHora: "",
};

export default function CasasPersonalAdmin() {
  const [casas,        setCasas]        = useState<Casa[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [showModal,    setShowModal]    = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Casa | null>(null);
  const [search,       setSearch]       = useState("");
  const [form,         setForm]         = useState(emptyForm);
  const [errors,       setErrors]       = useState<Record<string, string>>({});

  // Modal editar
  const [editTarget,   setEditTarget]   = useState<Casa | null>(null);
  const [editForm,     setEditForm]     = useState(emptyForm);
  const [editErrors,   setEditErrors]   = useState<Record<string, string>>({});
  const [updating,     setUpdating]     = useState(false);

  useEffect(() => {
    const unsub = subscribeToCasasPersonal((d) => { setCasas(d); setLoading(false); });
    return () => unsub();
  }, []);

  const canSave = form.nombreDueno.trim() && form.direccion.trim();

  const handleSubmit = async () => {
    const e: Record<string, string> = {};
    if (!form.nombreDueno.trim()) e.nombreDueno = "El nombre del dueño es obligatorio";
    if (!form.direccion.trim())   e.direccion   = "La dirección es obligatoria";
    if (Object.keys(e).length) { setErrors(e); return; }
    setSaving(true);
    try {
      const payload: any = {
        nombreDueno:   form.nombreDueno.trim(),
        direccion:     form.direccion.trim(),
        fechaRegistro: new Date(form.fechaRegistro + "T12:00:00"),
        activa:        true,
      };
      if (form.email.trim())    payload.email    = form.email.trim();
      if (form.telefono.trim()) payload.telefono = form.telefono.trim();
      if (form.precioHora)      payload.precioHora = Number(form.precioHora);
      await addCasaPersonal(payload);
      setShowModal(false); setForm(emptyForm); setErrors({});
    } finally { setSaving(false); }
  };

  // ── Editar ───────────────────────────────────────────────────────────────
  const openEdit = (casa: Casa) => {
    setEditTarget(casa);
    setEditForm({
      nombreDueno:   casa.nombreDueno,
      email:         casa.email      || "",
      telefono:      casa.telefono   || "",
      direccion:     casa.direccion  || "",
      fechaRegistro: toInput(casa.fechaRegistro),
      precioHora:    (casa as any).precioHora > 0 ? String((casa as any).precioHora) : "",
    });
    setEditErrors({});
  };

  const canUpdate = editForm.nombreDueno.trim() && editForm.direccion.trim();

  const handleUpdate = async () => {
    const e: Record<string, string> = {};
    if (!editForm.nombreDueno.trim()) e.nombreDueno = "El nombre del dueño es obligatorio";
    if (!editForm.direccion.trim())   e.direccion   = "La dirección es obligatoria";
    if (Object.keys(e).length) { setEditErrors(e); return; }
    if (!editTarget?.id) return;
    setUpdating(true);
    try {
      const payload: any = {
        nombreDueno: editForm.nombreDueno.trim(),
        direccion:   editForm.direccion.trim(),
        fechaRegistro: new Date(editForm.fechaRegistro + "T12:00:00"),
      };
      if (editForm.email.trim())    payload.email    = editForm.email.trim();
      if (editForm.telefono.trim()) payload.telefono = editForm.telefono.trim();
      if (editForm.precioHora)      payload.precioHora = Number(editForm.precioHora);
      await updateCasaPersonal(editTarget.id, payload);
      setEditTarget(null);
    } finally { setUpdating(false); }
  };

  const handleDelete = async () => {
    if (!deleteTarget?.id) return;
    await deleteCasaPersonal(deleteTarget.id);
    setDeleteTarget(null);
  };

  const filtered = casas.filter((c) =>
    `${c.nombreDueno} ${c.email ?? ""} ${c.direccion ?? ""}`.toLowerCase().includes(search.toLowerCase())
  );

  const activas  = casas.filter((c) => c.activa).length;
  const esteAnio = casas.filter((c) => c.fechaRegistro.getFullYear() === new Date().getFullYear()).length;

  return (
    <div className="flex-col lg:flex-row flex min-h-screen bg-slate-50">
      <SiderBar />

      <div className="flex-1 overflow-auto">
        {/* Header — rosa/fucsia igual que Pagos Personal */}
        <div className="bg-gradient-to-r from-rose-500 to-pink-500 px-8 py-8">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                <div className="w-7 h-7 bg-white/20 rounded-lg flex items-center justify-center">
                  <FaUser className="text-white" size={13} />
                </div>
                <span className="text-white/80 text-sm font-semibold uppercase tracking-widest">Persona Natural</span>
              </div>
              <h1 className="text-3xl font-bold text-white">Casas Personal</h1>
              <p className="text-rose-100 text-sm mt-1">
                Propiedades independientes · solo para servicios de persona natural
              </p>
            </div>
            <button
              onClick={() => { setShowModal(true); setForm(emptyForm); setErrors({}); }}
              className="flex items-center gap-2 bg-white text-rose-600 font-bold px-5 py-2.5 rounded-xl shadow-lg hover:bg-rose-50 transition-all text-[15px]"
            >
              <FaPlus size={14} /> Nueva Casa
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
            {[
              { icon: FaBuilding,    label: "Total casas",          value: casas.length },
              { icon: FaHome,        label: "Activas",              value: activas },
              { icon: FaCalendarAlt, label: "Registradas este año", value: esteAnio },
            ].map((s) => (
              <div key={s.label} className="bg-white/20 backdrop-blur rounded-2xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-white/30 rounded-xl flex items-center justify-center">
                  <s.icon className="text-white" size={18} />
                </div>
                <div>
                  <p className="text-rose-100 text-[13px] font-medium">{s.label}</p>
                  <p className="text-white text-2xl font-bold">{s.value}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="p-6 lg:p-8">
          {/* Búsqueda */}
          <div className="relative mb-6 max-w-md">
            <FaSearch className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={14} />
            <input
              type="text" placeholder="Buscar por nombre, email o dirección..."
              value={search} onChange={(e) => setSearch(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-white border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400 shadow-sm"
            />
          </div>

          {/* Tabla */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 overflow-hidden">
            {loading ? (
              <div className="flex items-center justify-center h-48">
                <div className="animate-spin w-8 h-8 border-4 border-rose-200 border-t-rose-500 rounded-full" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="flex flex-col items-center justify-center h-48 text-gray-400">
                <FaHome size={40} className="mb-3 text-gray-200" />
                <p className="font-medium text-[15px]">No hay casas registradas</p>
                <p className="text-[13px] mt-1">Agrega la primera con el botón de arriba</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50 border-b border-gray-100">
                      {["Dueño","Contacto","Dirección","Registro","$/hora","Estado",""].map((h, i) => (
                        <th key={i} className={`text-left px-6 py-4 text-[13px] font-semibold text-gray-500 uppercase tracking-wider ${i === 2 ? "hidden lg:table-cell" : i === 1 ? "hidden md:table-cell" : i === 4 ? "hidden sm:table-cell" : ""}`}>
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {filtered.map((casa) => (
                      <tr key={casa.id} onClick={() => openEdit(casa)} className="hover:bg-rose-50/30 transition-colors cursor-pointer group">
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-9 h-9 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 flex items-center justify-center text-white text-xs font-bold shrink-0">
                              {casa.nombreDueno.slice(0, 2).toUpperCase()}
                            </div>
                            <div>
                              <span className="text-[15px] font-semibold text-gray-800">{casa.nombreDueno}</span>
                              <span className="ml-2 opacity-0 group-hover:opacity-100 transition-opacity">
                                <FaEdit size={12} className="inline text-rose-400" />
                              </span>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4 hidden md:table-cell">
                          <div className="space-y-1">
                            {casa.email    && <span className="flex items-center gap-1.5 text-[13px] text-gray-500"><FaEnvelope size={10} className="text-gray-400" />{casa.email}</span>}
                            {casa.telefono && <span className="flex items-center gap-1.5 text-[13px] text-gray-500"><FaPhone size={10} className="text-gray-400" />{casa.telefono}</span>}
                            {!casa.email && !casa.telefono && <span className="text-gray-300 text-[15px]">—</span>}
                          </div>
                        </td>
                        <td className="px-6 py-4 hidden lg:table-cell">
                          {casa.direccion
                            ? <span className="flex items-center gap-1.5 text-[13px] text-gray-500"><FaMapMarkerAlt size={10} className="text-gray-400 shrink-0" /><span className="truncate max-w-[200px]">{casa.direccion}</span></span>
                            : <span className="text-gray-300 text-[15px]">—</span>}
                        </td>
                        <td className="px-6 py-4">
                          <span className="flex items-center gap-1.5 text-[13px] text-gray-500">
                            <FaCalendarAlt size={10} className="text-gray-400" />{fmt(casa.fechaRegistro)}
                          </span>
                        </td>
                        <td className="px-6 py-4 hidden sm:table-cell">
                          <span className="text-[15px] font-semibold text-gray-800">
                            {(casa as any).precioHora > 0 ? `$${(casa as any).precioHora.toFixed(2)}` : "—"}
                          </span>
                        </td>
                        <td className="px-6 py-4">
                          <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[13px] font-semibold ${casa.activa ? "bg-rose-100 text-rose-700" : "bg-gray-100 text-gray-500"}`}>
                            {casa.activa ? "Activa" : "Inactiva"}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-right">
                          <button onClick={(e) => { e.stopPropagation(); setDeleteTarget(casa); }} className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                            <FaTrash size={13} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Modal: Nueva Casa */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <FaUser size={11} className="text-rose-500" />
                  <span className="text-[13px] font-bold text-rose-500 uppercase tracking-wide">Persona Natural</span>
                </div>
                <h2 className="text-lg font-bold text-gray-800">Nueva Casa</h2>
                <p className="text-[13px] text-gray-400 mt-0.5">* Nombre del dueño y dirección son obligatorios</p>
              </div>
              <button onClick={() => setShowModal(false)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FaTimes size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Nombre del Dueño *</label>
                <input type="text" value={form.nombreDueno} onChange={(e) => setForm({ ...form, nombreDueno: e.target.value })} placeholder="John Smith"
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400 ${errors.nombreDueno ? "border-red-400" : "border-gray-200"}`} />
                {errors.nombreDueno && <p className="text-red-500 text-[13px] mt-1">{errors.nombreDueno}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Email</label>
                  <input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="cliente@email.com"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400" />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Teléfono</label>
                  <input type="tel" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} placeholder="(555) 123-4567"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400" />
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Dirección *</label>
                <input type="text" value={form.direccion} onChange={(e) => setForm({ ...form, direccion: e.target.value })} placeholder="123 Main St, City, State"
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400 ${errors.direccion ? "border-red-400" : "border-gray-200"}`} />
                {errors.direccion && <p className="text-red-500 text-[13px] mt-1">{errors.direccion}</p>}
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Fecha de Registro</label>
                <input type="date" value={form.fechaRegistro} onChange={(e) => setForm({ ...form, fechaRegistro: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Precio por hora ($)</label>
                <input type="number" min="0" step="0.01" value={form.precioHora} onChange={(e) => setForm({ ...form, precioHora: e.target.value })} placeholder="0.00"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400" />
              </div>
            </div>
            <p className="text-[13px] text-gray-400 text-center px-6 pb-2">* Obligatorios: Nombre del dueño y Dirección</p>
            <div className="flex gap-3 px-6 pb-6">
              <button onClick={() => setShowModal(false)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleSubmit} disabled={!canSave || saving}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold text-[15px] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-rose-200 hover:from-rose-600 hover:to-pink-600 transition-all">
                {saving ? "Guardando..." : "Guardar Casa"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Editar */}
      {editTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <div className="flex items-center gap-2 mb-0.5">
                  <FaEdit size={12} className="text-rose-500" />
                  <span className="text-[13px] font-bold text-rose-500 uppercase tracking-wide">Editar Casa Personal</span>
                </div>
                <h2 className="text-lg font-bold text-gray-800">{editTarget.nombreDueno}</h2>
                <p className="text-[13px] text-gray-400 mt-0.5">* Nombre del dueño y dirección son obligatorios</p>
              </div>
              <button onClick={() => setEditTarget(null)} className="p-2 rounded-lg hover:bg-gray-100 text-gray-500"><FaTimes size={16} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Nombre del Dueño *</label>
                <input type="text" value={editForm.nombreDueno} onChange={(e) => setEditForm({ ...editForm, nombreDueno: e.target.value })} placeholder="John Smith"
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400 ${editErrors.nombreDueno ? "border-red-400" : "border-gray-200"}`} />
                {editErrors.nombreDueno && <p className="text-red-500 text-[13px] mt-1">{editErrors.nombreDueno}</p>}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Email</label>
                  <input type="email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} placeholder="cliente@email.com"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400" />
                </div>
                <div>
                  <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Teléfono</label>
                  <input type="tel" value={editForm.telefono} onChange={(e) => setEditForm({ ...editForm, telefono: e.target.value })} placeholder="(555) 123-4567"
                    className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400" />
                </div>
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Dirección *</label>
                <input type="text" value={editForm.direccion} onChange={(e) => setEditForm({ ...editForm, direccion: e.target.value })} placeholder="123 Main St, City, State"
                  className={`w-full px-3 py-2.5 border rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400 ${editErrors.direccion ? "border-red-400" : "border-gray-200"}`} />
                {editErrors.direccion && <p className="text-red-500 text-[13px] mt-1">{editErrors.direccion}</p>}
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Fecha de Registro</label>
                <input type="date" value={editForm.fechaRegistro} onChange={(e) => setEditForm({ ...editForm, fechaRegistro: e.target.value })}
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400" />
              </div>
              <div>
                <label className="block text-[13px] font-semibold text-gray-600 mb-1.5">Precio por hora ($)</label>
                <input type="number" min="0" step="0.01" value={editForm.precioHora} onChange={(e) => setEditForm({ ...editForm, precioHora: e.target.value })} placeholder="0.00"
                  className="w-full px-3 py-2.5 border border-gray-200 rounded-xl text-[15px] focus:outline-none focus:ring-2 focus:ring-rose-400" />
              </div>
            </div>
            <div className="flex gap-3 px-6 pb-6 border-t border-gray-100 pt-4">
              <button onClick={() => setEditTarget(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleUpdate} disabled={!canUpdate || updating}
                className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-500 text-white font-bold text-[15px] disabled:opacity-40 disabled:cursor-not-allowed shadow-lg shadow-rose-200 hover:from-rose-600 hover:to-pink-600 transition-all">
                {updating ? "Actualizando..." : "Actualizar"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Eliminar */}
      {deleteTarget && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6 text-center">
            <div className="w-14 h-14 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4"><FaTrash size={20} className="text-red-500" /></div>
            <h3 className="text-lg font-bold text-gray-800 mb-2">¿Eliminar casa?</h3>
            <p className="text-gray-500 text-[15px] mb-6">Vas a eliminar la propiedad de <strong>{deleteTarget.nombreDueno}</strong>. Esta acción no se puede deshacer.</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteTarget(null)} className="flex-1 py-2.5 rounded-xl border border-gray-200 text-gray-600 font-semibold text-[15px] hover:bg-gray-50">Cancelar</button>
              <button onClick={handleDelete} className="flex-1 py-2.5 rounded-xl bg-red-500 text-white font-semibold text-[15px] hover:bg-red-600 shadow-lg shadow-red-200">Sí, eliminar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
